// KilometrinaMap.js — resilient version: error boundary + guarded async + safe overlays/RAF
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./KilometrinaMap.css";

/* ---------- Error Boundary keeps the page functional even if something throws ---------- */
class MapErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "" };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, message: error?.message || "Nepričakovana napaka." };
    }
    componentDidCatch(error, info) {
        console.error("[MapErrorBoundary]", error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="km-info" role="alert">
                    Prišlo je do napake pri prikazu zemljevida. Stran ostaja uporabljiva.
                    <br />
                    <small>Podrobnosti: {this.state.message}</small>
                </div>
            );
        }
        return this.props.children;
    }
}

const KM_MAP_STYLE = [
    { elementType: "geometry", stylers: [{ color: "#071125" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#9db0df" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#071125" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: "#0d1b39" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#0e1e40" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ff7b7b" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#233d79" }] },
    { featureType: "transit", elementType: "geometry", stylers: [{ color: "#0d1b39" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a1633" }] },
    { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9bb0ff" }] },
    { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#89a7ff" }, { weight: 0.2 }] },
];

const COUNTRY_SUFFIX = "Slovenia";
const DEFAULT_RATE = 0.43;
const WORLD_BOUNDS = { north: 85, south: -85, west: -179.999, east: 179.999 };

// Wait until google.maps.importLibrary exists (deterministic, with timeout)
async function waitForImportLibrary(timeoutMs = 12000) {
    const start = performance.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const g = typeof window !== "undefined" ? window.google : undefined;
        if (g?.maps?.importLibrary) return g.maps.importLibrary;
        if (performance.now() - start > timeoutMs) {
            throw new Error("Google Maps loader did not initialize in time.");
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 50));
    }
}

function getActiveTheme() {
    try {
        if (typeof document === "undefined") return "light";

        const htmlTheme = document.documentElement.getAttribute("data-theme");
        const bodyTheme = document.body?.getAttribute("data-theme");

        let storedTheme = "";
        try {
            storedTheme = localStorage.getItem("theme") || "";
        } catch {
            storedTheme = "";
        }

        return htmlTheme || bodyTheme || storedTheme || "light";
    } catch {
        return "light";
    }
}

/* ---------- Main component (inner) ---------- */
function KilometrinaMapInner({ onCalculated }) {
    const mapElRef = useRef(null);
    const didInitRef = useRef(false);

    const dirRendererRef = useRef(null);
    const glowLayerRef = useRef(null);
    const borderLayerRef = useRef(null);

    const [map, setMap] = useState(null);

    const [activeTheme, setActiveTheme] = useState(() => getActiveTheme());
    const isDarkTheme = activeTheme === "dark";

    useEffect(() => {
        function updateTheme() {
            setActiveTheme(getActiveTheme());
        }

        updateTheme();

        if (typeof MutationObserver === "undefined") {
            return undefined;
        }

        const observer = new MutationObserver(updateTheme);

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme", "class"]
        });

        if (document.body) {
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ["data-theme", "class"]
            });
        }

        window.addEventListener("storage", updateTheme);
        window.addEventListener("themechange", updateTheme);

        return () => {
            observer.disconnect();
            window.removeEventListener("storage", updateTheme);
            window.removeEventListener("themechange", updateTheme);
        };
    }, []);

    // Inputs
    const [fromStreet, setFromStreet] = useState("");
    const [fromCity, setFromCity] = useState("Dunajska cesta 18,Ljubljana");
    const [toStreet, setToStreet] = useState("");
    const [toCity, setToCity] = useState("Usnjarska ulica 10,Maribor");

    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    // Fuel price -> 30%
    const [fuelInput, setFuelInput] = useState("");

    // Maps loading/error UI
    const [mapsLoading, setMapsLoading] = useState(true);
    const [mapsError, setMapsError] = useState("");

    // Protect against stale results
    const reqIdRef = useRef(0);

    const navigate = useNavigate();

    const FUEL_PRICE_URL = "https://www.gov.si/teme/cene-naftnih-derivatov/";

    // ===== Helpers =====
    const sanitizeFuelPrice = (raw) => {
        const s = String(raw ?? "").replace(",", ".");
        const only = s.replace(/[^0-9.]/g, "");
        if (only.includes(".")) {
            const parts = only.split(".");
            return parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : only;
        }
        if (only.length >= 4) return only.slice(0, -3) + "." + only.slice(-3);
        return only;
    };
    const handleFuelChange = (e) => setFuelInput(sanitizeFuelPrice(e.target.value));

    const fuelPrice = parseFloat(fuelInput);
    const rateFromFuel = Number.isFinite(fuelPrice) ? fuelPrice * 0.30 : NaN;
    const appliedRate = Number.isFinite(rateFromFuel) ? Number(rateFromFuel.toFixed(2)) : DEFAULT_RATE;

    const formatEUR = (v) =>
        new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v);
    const formatNum = (v, max = 3) =>
        new Intl.NumberFormat("sl-SI", { maximumFractionDigits: max }).format(v);

    // ========= Route draw animation =========
    const progressGlowRef = useRef(null);
    const progressLineRef = useRef(null);
    const runnerMarkerRef = useRef(null);
    const animRAFRef = useRef(null);

    // LIVE km counter INSIDE the result window
    const kmLiveRef = useRef(null);
    const updateInfoKm = (km) => {
        if (kmLiveRef.current) {
            const val = Math.max(0, Number(km) || 0);
            kmLiveRef.current.textContent = `${val.toFixed(1)} km`;
        }
    };

    const stopRouteAnimation = (removeFinalLine = true) => {
        try { if (animRAFRef.current) cancelAnimationFrame(animRAFRef.current); } catch (e) { console.warn(e); }
        animRAFRef.current = null;

        try { runnerMarkerRef.current?.setMap?.(null); } catch (e) { console.warn(e); }
        runnerMarkerRef.current = null;

        if (removeFinalLine) {
            try { progressGlowRef.current?.setMap?.(null); } catch (e) { console.warn(e); }
            progressGlowRef.current = null;
            try { progressLineRef.current?.setMap?.(null); } catch (e) { console.warn(e); }
            progressLineRef.current = null;
        }
        mapElRef.current?.classList.remove("km-map--animating");
    };

    const toRad = (deg) => (deg * Math.PI) / 180;
    const distMeters = (a, b) => {
        const R = 6371000;
        const dLat = toRad(b.lat - a.lat);
        const dLng = toRad(b.lng - a.lng);
        const sa = Math.sin(dLat / 2), sb = Math.sin(dLng / 2);
        const c = 2 * Math.asin(Math.min(1, Math.sqrt(sa * sa + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sb * sb)));
        return R * c;
    };
    const lerp = (a, b, t) => a + (b - a) * t;
    const interpolate = (p1, p2, t) => ({ lat: lerp(p1.lat, p2.lat, t), lng: lerp(p1.lng, p2.lng, t) });
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    // --- Create runner pointer (AdvancedMarkerElement if available; otherwise DOM Overlay) ---
    const createRunnerPointer = (MapClasses, m, position) => {
        const { AdvancedMarkerElement, OverlayView } = MapClasses;

        const supportsAdvanced =
            typeof m?.getMapCapabilities === "function" &&
            !!m.getMapCapabilities()?.isAdvancedMarkersAvailable &&
            !!AdvancedMarkerElement;

        if (supportsAdvanced) {
            const el = document.createElement("div");
            el.className = "km-runner-dot";
            const adv = new AdvancedMarkerElement({
                map: m,
                position,
                content: el,
                zIndex: 1001,
            });
            return {
                setMap: (mapOrNull) => { adv.map = mapOrNull; },
                setPosition: (pos) => { adv.position = pos; },
            };
        }

        // DOM Overlay (no Marker; no Map ID required; no warnings)
        const overlay = new OverlayView();
        overlay.position = position;
        overlay.div = null;

        overlay.onAdd = function () {
            try {
                const div = document.createElement("div");
                div.className = "km-runner-dot";
                div.style.position = "absolute";
                div.style.transform = "translate(-50%, -50%)";
                this.div = div;
                this.getPanes().overlayMouseTarget.appendChild(div);
            } catch (e) {
                console.warn("[overlay.onAdd]", e);
            }
        };

        overlay.draw = function () {
            try {
                if (!this.div) return;
                const proj = this.getProjection();
                if (!proj || !this.position) return;
                const pt = proj.fromLatLngToDivPixel(
                    new window.google.maps.LatLng(this.position.lat, this.position.lng)
                );
                if (!pt) return;
                this.div.style.left = pt.x + "px";
                this.div.style.top = pt.y + "px";
            } catch (e) {
                console.warn("[overlay.draw]", e);
            }
        };

        overlay.onRemove = function () {
            try {
                if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
                this.div = null;
            } catch (e) {
                console.warn("[overlay.onRemove]", e);
            }
        };

        try { overlay.setMap(m); } catch (e) { console.warn("[overlay.setMap]", e); }

        return {
            setMap: (mapOrNull) => { try { overlay.setMap(mapOrNull); } catch (e) { console.warn(e); } },
            setPosition: (pos) => { overlay.position = pos; try { overlay.draw?.(); } catch (e) { console.warn(e); } },
        };
    };

    const startRouteAnimation = (MapClasses, route) => {
        if (!map || !route) return;

        const { Polyline } = MapClasses;

        // Build path from overview (fallback to steps)
        let raw = [];
        try {
            if (Array.isArray(route.overview_path) && route.overview_path.length) {
                raw = route.overview_path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
            } else if (Array.isArray(route.legs)) {
                route.legs.forEach((leg) => {
                    leg?.steps?.forEach((s) => {
                        if (Array.isArray(s.path)) raw.push(...s.path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() })));
                    });
                });
            }
        } catch (e) {
            console.warn("[build path]", e);
        }
        if (raw.length < 2) return;

        // Reset previous draw
        stopRouteAnimation(true);

        // Precompute cumulative distances
        const cum = [0];
        for (let i = 1; i < raw.length; i++) {
            const d = distMeters(raw[i - 1], raw[i]);
            cum.push(cum[i - 1] + (isFinite(d) ? d : 0));
        }
        const total = cum[cum.length - 1] || 1;

        // FX
        mapElRef.current?.classList.add("km-map--animating");

        let glow, line, runner;
        try {
            glow = new Polyline({
                map,
                path: [raw[0]],
                geodesic: true,
                strokeColor: "#7CFC9A",
                strokeOpacity: 0.18,
                strokeWeight: 7,
                zIndex: 998,
            });
            progressGlowRef.current = glow;

            line = new Polyline({
                map,
                path: [raw[0]],
                geodesic: true,
                strokeColor: "#7CFC9A",
                strokeOpacity: 0.95,
                strokeWeight: 5,
                zIndex: 999,
            });
            progressLineRef.current = line;

            runner = createRunnerPointer(MapClasses, map, raw[0]);
            runnerMarkerRef.current = runner;
        } catch (e) {
            console.warn("[create polylines/runner]", e);
        }

        // Reduced motion: draw instantly
        const prefersReducedMotion =
            typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (prefersReducedMotion) {
            try {
                glow?.setPath?.(raw);
                line?.setPath?.(raw);
                runner?.setMap?.(null);
            } catch (e) { console.warn(e); }
            runnerMarkerRef.current = null;
            mapElRef.current?.classList.remove("km-map--animating");
            updateInfoKm(total / 1000);
            return;
        }

        const DURATION = Math.min(3500, Math.max(900, total / 3.0));
        const t0 = performance.now();

        const buildPathAt = (s) => {
            const out = [raw[0]];
            if (s <= 0) return out;
            let i = 1;
            while (i < raw.length && cum[i] < s) { out.push(raw[i]); i++; }
            if (i < raw.length) {
                const segLen = Math.max(0.0001, cum[i] - cum[i - 1]);
                const remain = s - cum[i - 1];
                const frac = Math.max(0, Math.min(1, remain / segLen));
                out.push(interpolate(raw[i - 1], raw[i], frac));
            }
            return out;
        };

        updateInfoKm(0);

        const tick = (ts) => {
            try {
                const t = Math.min(1, (ts - t0) / DURATION);
                const eased = easeInOutCubic(t);
                const progressed = eased * total;

                updateInfoKm(progressed / 1000);

                const partial = buildPathAt(progressed);
                glow?.setPath?.(partial);
                line?.setPath?.(partial);

                const head = partial[partial.length - 1];
                try { runner?.setPosition?.(head); } catch (e) { console.warn(e); }

                if (t < 1) {
                    animRAFRef.current = requestAnimationFrame(tick);
                } else {
                    setTimeout(() => {
                        try { runnerMarkerRef.current?.setMap?.(null); } catch (e) { console.warn(e); }
                        runnerMarkerRef.current = null;
                        try { if (animRAFRef.current) cancelAnimationFrame(animRAFRef.current); } catch (e) { console.warn(e); }
                        animRAFRef.current = null;
                        mapElRef.current?.classList.remove("km-map--animating");
                        updateInfoKm(total / 1000);
                    }, 200);
                }
            } catch (e) {
                console.warn("[animation tick]", e);
                stopRouteAnimation(false);
            }
        };

        try {
            animRAFRef.current = requestAnimationFrame(tick);
        } catch (e) {
            console.warn("[requestAnimationFrame]", e);
        }
    };
    // ========= /Route draw animation =========

    // ===== Map init (robust, modular) =====
    const initMap = async () => {
        if (didInitRef.current) return; // guard double init (StrictMode/dev)
        didInitRef.current = true;

        setMapsLoading(true);
        setMapsError("");

        try {
            const importLibrary = await waitForImportLibrary();

            // Load libraries and destructure constructors from modules
            const [
                { Map, Data, Polyline, OverlayView },
                { DirectionsService, DirectionsRenderer, TravelMode },
                { AdvancedMarkerElement } = {},
            ] = await Promise.all([
                importLibrary("maps"),
                importLibrary("routes"),
                importLibrary("marker").catch(() => ({})), // optional
            ]);

            if (!mapElRef.current) throw new Error("Map container not mounted.");

            const m = new Map(mapElRef.current, {
                center: { lat: 46.1512, lng: 14.9955 },
                zoom: 8,
                styles: isDarkTheme ? KM_MAP_STYLE : [],
                fullscreenControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                backgroundColor: isDarkTheme ? "#071125" : "#f8fafc",
                restriction: { latLngBounds: WORLD_BOUNDS, strictBounds: true },
                keyboardShortcuts: false,
                rotateControl: false,
                disableDefaultUI: true,
            });
            setMap(m);

            // Border layers (optional, guarded)
            try {
                const res = await fetch("/assets/slovenia.geojson");
                if (res.ok) {
                    const geo = await res.json();

                    const glow = new Data({ map: m });
                    glow.addGeoJson(geo);
                    glow.setStyle({
                        strokeColor: "#74a6ff",
                        strokeOpacity: 0.25,
                        strokeWeight: 10,
                        fillColor: "#6ea8ff",
                        fillOpacity: 0.05,
                    });
                    glowLayerRef.current = glow;

                    const border = new Data({ map: m });
                    border.addGeoJson(geo);
                    border.setStyle({
                        strokeColor: "#9bb0ff",
                        strokeOpacity: 0.9,
                        strokeWeight: 2,
                        fillColor: "#6ea8ff",
                        fillOpacity: 0.03,
                    });
                    borderLayerRef.current = border;

                    // Fit to border
                    const bounds = new window.google.maps.LatLngBounds();
                    border.forEach((feature) => {
                        feature.getGeometry()?.forEachLatLng((ll) => bounds.extend(ll));
                    });
                    if (!bounds.isEmpty()) m.fitBounds(bounds, { top: 16, right: 16, bottom: 16, left: 16 });
                }
            } catch (e) {
                console.warn("[geojson]", e);
            }

            // DirectionsRenderer: NO polylines/markers — we draw our own
            try {
                if (!dirRendererRef.current) {
                    dirRendererRef.current = new DirectionsRenderer({
                        map: m,
                        suppressPolylines: true,
                        suppressMarkers: true,
                        preserveViewport: false,
                    });
                } else {
                    dirRendererRef.current.setMap(m);
                }
            } catch (e) {
                console.warn("[DirectionsRenderer]", e);
            }

            // Cache constructors for later
            mapElRef.current.__km_classes = {
                Map, Data, Polyline, OverlayView,
                DirectionsService, DirectionsRenderer, TravelMode,
                AdvancedMarkerElement,
            };

            setMapsLoading(false);
        } catch (e) {
            console.error("[initMap]", e);
            setMapsError(
                e?.message ||
                "Zemljevida Google Maps trenutno ni mogoče naložiti. Preverite povezavo in poskusite znova."
            );
            setMapsLoading(false);
        }
    };

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                await initMap();
            } catch (e) {
                if (alive) {
                    console.error("[init effect]", e);
                    setMapsError(e?.message || "Google Maps se ni naložil. Preverite povezavo in poskusite znova.");
                    setMapsLoading(false);
                }
            }
        })();

        // Global safety net: show inline error but never crash UI
        const onUnhandledRejection = (ev) => {
            console.error("[unhandledrejection]", ev);
            setMapsError((prev) => prev || ev?.reason?.message || "Nepričakovana napaka.");
        };
        const onWindowError = (ev) => {
            console.error("[window error]", ev);
            setMapsError((prev) => prev || ev?.message || "Nepričakovana napaka.");
        };
        window.addEventListener("unhandledrejection", onUnhandledRejection);
        window.addEventListener("error", onWindowError);

        return () => {
            alive = false;
            window.removeEventListener("unhandledrejection", onUnhandledRejection);
            window.removeEventListener("error", onWindowError);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            try { dirRendererRef.current?.setMap(null); } catch (e) { console.warn(e); }
            dirRendererRef.current = null;
            try { glowLayerRef.current?.setMap(null); } catch (e) { console.warn(e); }
            glowLayerRef.current = null;
            try { borderLayerRef.current?.setMap(null); } catch (e) { console.warn(e); }
            borderLayerRef.current = null;
            stopRouteAnimation(true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!map) return;

        map.setOptions({
            styles: isDarkTheme ? KM_MAP_STYLE : [],
            backgroundColor: isDarkTheme ? "#071125" : "#f8fafc",
        });

        // Force Google Maps repaint after theme switch
        window.google?.maps?.event?.trigger?.(map, "resize");

        const center = map.getCenter();
        if (center) {
            map.setCenter(center);
        }
    }, [map, isDarkTheme]);

    const buildAddress = (street, city) => {
        const combined = [street, city]
            .filter(Boolean)
            .join(", ")
            .replace(/\s*,\s*/g, ", ")
            .replace(/\s+/g, " ")
            .trim();

        if (!combined) return COUNTRY_SUFFIX;

        return /(slovenia|slovenija)$/i.test(combined)
            ? combined
            : `${combined}, ${COUNTRY_SUFFIX}`;
    };

    const calculate = async () => {
        if (!map || !mapElRef.current?.__km_classes) {
            setMapsError("Zemljevid se še nalaga. Poskusite znova čez trenutek.");
            return;
        }
        const { DirectionsService, DirectionsRenderer, TravelMode } = mapElRef.current.__km_classes;

        const myReq = ++reqIdRef.current;
        setBusy(true);
        setMapsError("");
        setResult(null);

        let svc;
        try {
            svc = new DirectionsService();
        } catch (e) {
            console.error("[DirectionsService]", e);
            setBusy(false);
            setMapsError("Storitev za izračun poti trenutno ni na voljo. Poskusite znova kasneje.");
            return;
        }

        try {
            if (!dirRendererRef.current) {
                dirRendererRef.current = new DirectionsRenderer({
                    map,
                    suppressPolylines: true,
                    suppressMarkers: true,
                    preserveViewport: false,
                });
            } else {
                dirRendererRef.current.setMap(map);
            }
        } catch (e) {
            console.warn(e);
        }

        const origin = buildAddress(fromStreet, fromCity);
        const destination = buildAddress(toStreet, toCity);

        try {
            const resp = await svc.route({
                origin,
                destination,
                travelMode: TravelMode.DRIVING,
                provideRouteAlternatives: false,
            });

            if (myReq !== reqIdRef.current) return; // ignore stale responses

            try { dirRendererRef.current?.setDirections(resp); } catch (e) { console.warn(e); }

            const route = resp?.routes?.[0];
            const leg = route?.legs?.[0];

            if (leg?.distance?.value != null && leg?.duration?.value != null) {
                const finalKm = leg.distance.value / 1000;
                const finalMin = leg.duration.value / 60;
                setResult({ distanceKm: finalKm, durationMin: finalMin });

                // ✅ NEW: report result to parent (Stroskovnik) without changing existing behavior
                const safeRate = Number(appliedRate) || 0;
                const costEUR = (Number(finalKm) || 0) * safeRate;
                if (typeof onCalculated === "function") {
                    onCalculated({
                        distanceKm: finalKm,
                        durationMin: finalMin,
                        rateEURPerKm: safeRate,
                        costEUR,
                    });
                }

                setTimeout(() => {
                    updateInfoKm(0);
                    try { startRouteAnimation(mapElRef.current.__km_classes, route); } catch (e) { console.warn(e); }
                }, 0);
            } else {
                try { startRouteAnimation(mapElRef.current.__km_classes, route); } catch (e) { console.warn(e); }
            }
        } catch (err) {
            if (myReq !== reqIdRef.current) return; // stale
            const msg =
                (err && err.message) ||
                "Ne morem izračunati poti. Preverite vnesene naslove in poskusite znova.";
            setMapsError(msg);
            stopRouteAnimation(true);
        } finally {
            if (myReq === reqIdRef.current) setBusy(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            calculate();
        }
    };

    const combinedValue = (street, city) =>
        [street, city].filter((p) => p && p.trim().length).join(", ");
    const handleCombinedChange = (raw, setStreet, setCity) => {
        setStreet(raw);
        setCity("");
    };

    return (
        <div className="km-wrap">
            {/* Map + loading overlay */}
            <div className="km-mapwrap">
                <div ref={mapElRef} className="km-map" />
                {mapsLoading && (
                    <div className="km-map__overlay" aria-live="polite">
                        <div className="km-spinner" />
                        Nalaganje zemljevida …
                    </div>
                )}
            </div>

            {mapsError && (
                <div className="km-info" role="alert">
                    {mapsError}
                </div>
            )}

            <div className="km-bar">
                {/* FROM */}
                <div className="km-field">
                    <div className="km-hint">Začetni naslov (OD)</div>
                    <input
                        className="km-inp"
                        value={combinedValue(fromStreet, fromCity)}
                        onChange={(e) => handleCombinedChange(e.target.value, setFromStreet, setFromCity)}
                        onKeyDown={handleKeyDown}
                        placeholder="npr. Ulica 123, Ljubljana"
                        autoComplete="off"
                        inputMode="text"
                    />
                </div>

                {/* Arrow */}
                <div className="km-arrow" aria-hidden="true">
                    →
                </div>

                {/* TO */}
                <div className="km-field">
                    <div className="km-hint">Ciljni naslov (DO)</div>
                    <input
                        className="km-inp"
                        value={combinedValue(toStreet, toCity)}
                        onChange={(e) => handleCombinedChange(e.target.value, setToStreet, setToCity)}
                        onKeyDown={handleKeyDown}
                        placeholder="npr. Ulica 123, Maribor"
                        autoComplete="off"
                        inputMode="text"
                    />
                </div>

                {/* Fuel price -> auto €/km (30%) */}
                <div className="km-field km-field--percent">
                    <div className="km-hint">
                        Trenutna cena goriva{" "}
                        <a
                            className="km-link"
                            href={FUEL_PRICE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Odpri uradni cenik goriv v novem zavihku"
                        >
                            (uradni cenik)
                        </a>
                    </div>
                    <div className="km-percentrow">
                        <input
                            className="km-inp"
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*[.,]?[0-9]*"
                            value={fuelInput}
                            onChange={handleFuelChange}
                            placeholder="npr. 1,438 €/l"
                            autoComplete="off"
                            aria-label="Trenutna cena goriva"
                        />
                        <div className="km-badge" title="Za izračun se uporabi 30 % od cene goriva">
                            €/km{" "}
                            <strong>
                                {Number.isFinite(rateFromFuel)
                                    ? formatNum(rateFromFuel, 3)
                                    : formatNum(DEFAULT_RATE, 2)}
                            </strong>
                        </div>
                        <button className="km-btn" onClick={calculate} disabled={busy}>
                            {busy ? "Izračunavam …" : "Izračunaj pot"}
                        </button>
                    </div>
                </div>
            </div>

            {result && (
                <div className="km-info" role="status" aria-live="polite">
                    <div className="km-info__line">
                        Razdalja poti:{" "}
                        <strong ref={kmLiveRef} className="km-km">
                            {Number(result.distanceKm).toFixed(1)} km
                        </strong>
                    </div>
                    <div className="km-info__line">
                        Predviden čas vožnje: <strong>{Number(result.durationMin).toFixed(0)} min</strong>
                    </div>
                    <div className="km-info__line">
                        Okvirni strošek kilometrine:{" "}
                        <strong>
                            {formatEUR((Number(result.distanceKm) || 0) * (Number(appliedRate) || 0))}
                        </strong>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------- Default export wrapped in an error boundary ---------- */
export default function KilometrinaMap({ onCalculated }) {
    return (
        <MapErrorBoundary>
            <KilometrinaMapInner onCalculated={onCalculated} />
        </MapErrorBoundary>
    );
}
